import type Session from "../interfaces/Session";
import {
  type DuoSession,
  DuoStatus,
  type Group as SessionGroup,
} from "../interfaces/Session";
import type { RawExtra, UserDuo } from "../interfaces/User";
import {
  type Group,
  type Student,
  type Teacher,
  UserType,
} from "../interfaces/User";
import { effectiveFragments } from "./fragments";
import { completeLockStatus } from "./pedagogical-ressources";
import { classroomAlphabeticalSort, userAlphabeticalSort } from "./user";

export const userToSession = (
  user: Teacher | Student,
  userType: UserType,
  isSpecimen: boolean | undefined,
): Partial<Session> => {
  const common = {
    userId: user.id,
    firstname: user.first_name,
    lastname: user.last_name,
    userProvider: user.provider,
    appProvider: user.provider,
    fragments: effectiveFragments(user),
    userType: isSpecimen ? UserType.Student : userType,
    extra: formatExtra(user.extra),
  };
  if (userType === UserType.Teacher)
    return {
      ...common,
      classrooms: formatGroups((user as Teacher).classrooms),
      groups: formatGroups((user as Teacher).groups),
    };
  else if (userType === UserType.Student)
    return {
      ...common,
      duos: combineMentorMentee(user as Student),
      resourceAssignments: (user as Student).resources_assignments?.filter(
        (assignment) => !!assignment.assigned_resource,
      ) as Session["resourceAssignments"],
      classroomIds: user.classrooms as string[],
    };
  return common;
};

export const formatExtra = (extra: RawExtra | undefined) => {
  return !extra || extra === "none" || extra === "null" ? {} : extra;
};

const formatGroups = (groups: Group[]): Session["classrooms"] => {
  return groups
    .map((group) => ({
      id: group.id,
      name: group.name,
      lockStatus: completeLockStatus(group.lockStatus),
      students: group.students
        .map((student) => ({
          id: student.id,
          firstname: student.first_name,
          lastname: student.last_name,
          extra: formatExtra(student.extra),
        }))
        .sort(userAlphabeticalSort),
      external_id: group.external_id,
    }))
    .sort(classroomAlphabeticalSort);
};

export const combineMentorMentee = ({
  mentor_sessions = [],
  mentee_sessions = [],
}: {
  mentor_sessions?: UserDuo[];
  mentee_sessions?: UserDuo[];
}): DuoSession[] => {
  const duos: DuoSession[] = [];
  for (const session of [...mentee_sessions, ...mentor_sessions]) {
    if (duos.every((duo) => duo.id !== session.id))
      duos.push(userDuoToSessionDuo(session));
  }
  return duos;
};

export const userDuoToSessionDuo = (duo: UserDuo): DuoSession => ({
  id: duo.id,
  name: duo.name,
  mentor: {
    firstname: duo.mentor.first_name,
    lastname: duo.mentor.last_name,
    id: duo.mentor.id,
    extra: duo.mentor.extra,
  },
  mentee: {
    firstname: duo.mentee.first_name,
    lastname: duo.mentee.last_name,
    id: duo.mentee.id,
    extra: duo.mentee.extra,
  },
  moduleId: duo.config.module_id,
  status: userDuoStatusToSessionDuoStatus(duo.status),
  teacherId: duo.teacher,
});

const userDuoStatusToSessionDuoStatus = (
  status: UserDuo["status"],
): DuoSession["status"] => {
  switch (status) {
    case "inactive":
      return DuoStatus.Inactive;
    case "active":
      return DuoStatus.Active;
    case "closed":
      return DuoStatus.Completed;
    default: {
      /* eslint-disable-next-line */
      const _: never = status;
      return DuoStatus.Inactive; // TODO
    }
  }
};

export const hasStudents = (classrooms: SessionGroup[] | undefined): boolean =>
  classrooms
    ? classrooms.some((classroom) => classroom.students.length > 0)
    : false;
